import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import CustomField from '@enview/interface/types/CustomFields';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getTeamsWithBillFieldValue,
  lookupCustomFieldValue,
} from '../../../helpers/BillHelper';
import { getSelectedTeamIds, getTeamMode } from '../../../dux';
import { useSelector } from 'react-redux';
import RichTextEditor from '../ActivityFeed/CommentForm/RichTextEditor';
import { Descendant } from 'slate';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import { CustomFieldsAPI } from '../../../api';
import './BillCustomField.scss';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import TeamSelector from '../TeamSelector';
import { CUSTOM_FIELD_RICH_TEXT_CHAR_LIMIT } from '../../../utils/constants';
import { Spinner } from 'react-bootstrap';
import { nodeHasText } from '../../../helpers/RichTextHelpers';

interface BillCustomFieldValueProps {
  bill: BillSummary;
  field: CustomField;
}

const CONFRIM_POPOVER_TITLE = 'Select the teams you want to share with:';

export const BillCustomFieldValue: React.FC<BillCustomFieldValueProps> = ({
  bill,
  field,
}) => {
  const { title, orgId } = field;
  const teamModeId = useSelector(getTeamMode);
  const nodeRef = useRef<any>();
  const [comment, setComment] = useState<Descendant[]>([]);
  const [displaySavePopup, setDisplaySavePopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [shouldArchive, setShouldArchive] = useState(false);
  const selectedTeamIds = useSelector(getSelectedTeamIds);

  const [upsertCustomFieldValue, { isLoading }] =
    CustomFieldsAPI.endpoints.upsertCustomFieldValue.useMutation();

  const nodeHasValue = useMemo(() => {
    return nodeHasText({ children: comment });
  }, [comment]);

  const onChangeToEditor = (event: Descendant[]) => {
    setComment(event);
  };

  const billValue = useMemo(
    () => lookupCustomFieldValue(bill.customFields ?? {}, title, orgId, teamModeId),
    [bill.customFields, title, orgId, teamModeId],
  );

  const teamsWithBillFieldValue = useMemo(
    () =>
      getTeamsWithBillFieldValue(bill.customFields ?? {}, title, orgId, billValue?.id),
    [bill.customFields, title, orgId, billValue?.id],
  );

  const hasValueSet = !!billValue?.fieldValue;

  const getUserAction = useMemo(() => {
    if (comment && comment.length) {
      return {
        commentJson: { children: comment },
      } as Partial<UserAction>;
    }

    if (billValue?.fieldValue?.richText) {
      return {
        commentJson: billValue.fieldValue.richText,
      } as Partial<UserAction>;
    }
  }, [comment, billValue?.fieldValue]);

  const onPostConfirm = useCallback(
    async (teams?: number[]) => {
      if (!orgId) return;

      const update = {
        fieldId: field.id,
        billId: bill.id,
        data: {
          teams: teams ? teams : selectedTeamIds,
          value: {
            richText: { children: comment },
          },
          orgId,
          shouldArchive,
          ...(hasValueSet && billValue?.id ? { valueId: billValue.id } : {}),
        },
      };

      if (shouldArchive || nodeHasValue) {
        await upsertCustomFieldValue(update);
      }
      setIsEditMode(false);
      setDisplaySavePopup(false);
      setShouldArchive(false);
    },
    [selectedTeamIds, comment, shouldArchive, hasValueSet, billValue?.id, nodeHasValue],
  );

  useEffect(() => {
    if (!isEditMode) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;

      if (target?.closest('.post-confirmation')) {
        return;
      }

      if (nodeRef.current && !nodeRef.current.contains(e.target as Node)) {
        // save to current team on click outside
        onPostConfirm([teamModeId]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditMode, onPostConfirm]);

  const onSaveClicked = () => {
    // TODO: add feature gating
    if (!comment && typeof comment !== 'object') return;
    setDisplaySavePopup(true);
  };

  const beginEdit = () => {
    setIsEditMode(true);
  };

  const saveButton = (
    <ConfirmActionPopover
      align="end"
      closePopup={() => setDisplaySavePopup(false)}
      content={
        <div className="post-confirmation">
          <p className="popover-confirm-body mt-1">{CONFRIM_POPOVER_TITLE}</p>
          <TeamSelector
            excludePersonalWorkspace={true}
            initialSelection={teamsWithBillFieldValue}
          />
          <div className="text-center mt-3">
            <button
              className="btn btn-sm btn-block btn-action"
              onClick={() => onPostConfirm()}
              type="button"
            >
              Save
            </button>
          </div>
        </div>
      }
      positions={['left', 'top']}
      show={displaySavePopup}
      target={
        <div className="action-buttons">
          <button
            className="btn btn-link btn-sm cf-btn mr-2"
            disabled={!comment}
            onClick={() => {
              setShouldArchive(true);
              onSaveClicked();
            }}
            type="button"
          >
            Delete
          </button>

          <button
            className="btn btn-primary btn-sm cf-btn"
            disabled={!comment}
            onClick={onSaveClicked}
            type="button"
          >
            Save
          </button>
        </div>
      }
      targetStyle={{ width: '100%' }}
      toggleCallback={() => {}}
    />
  );

  if (isLoading) {
    return <Spinner animation="border" role="status"></Spinner>;
  } else if (!isEditMode) {
    return (
      <div className="rich-text-editor" id="cf-rich-text" onClick={beginEdit}>
        <RichTextEditor
          disabled
          onChangeToEditor={onChangeToEditor}
          placeholder={''}
          readOnly
          style={{ padding: '12px', maxHeight: '120px', overflowY: 'auto' }}
          userAction={getUserAction}
        />
      </div>
    );
  } else {
    return (
      <div className="rich-text-editor" id="cf-rich-text" ref={nodeRef}>
        <RichTextEditor
          charLimit={CUSTOM_FIELD_RICH_TEXT_CHAR_LIMIT}
          onChangeToEditor={onChangeToEditor}
          placeholder={''}
          style={{ padding: '12px', maxHeight: '150px', overflowY: 'auto' }}
          userAction={getUserAction}
        />
        <div className="d-flex flex-row my-2 ml-auto">{saveButton}</div>
      </div>
    );
  }
};

export default BillCustomFieldValue;
